import { ChangeDetectorRef, Component, QueryList, ViewChildren } from '@angular/core';
import { EngagementsState, SaveEngagementLetter, SaveEngagementLetterMultiContact } from '../../state';
import { Annotation, Logger, SharedModule, ViewAndSignPdfComponent } from '@aiwyn/shared';
import { Store } from '@ngxs/store';
import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AbstractApryseStep } from '../abstract-apryse-step/abstract-apryse-step';
import { EngagementTask, EngagementTaskType, SignatureRequirementType } from '@aiwyn/api';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '@aiwyn/auth';
import { STEP_LABEL_DOCUMENT_TYPE_MAPPING } from '../../models';
import { getAnnotationDocumentContactId } from '../abstract-apryse-step/apryse-utils';

@Component({
  selector: 'app-engagement-letter-step',
  templateUrl: '../abstract-apryse-step/abstract-apryse-step.html',
  styleUrls: [
    './engagement-letter-step.component.scss',
    '../abstract-apryse-step/abstract-apryse-step.scss',
  ],
  standalone: true,
  imports: [
    SharedModule,
    NgClass,
    AsyncPipe,
  ],
})
export class EngagementLetterStepComponent extends AbstractApryseStep {

  @ViewChildren(ViewAndSignPdfComponent) pdfViewer: QueryList<ViewAndSignPdfComponent>;

  constructor(
    readonly store: Store,
    readonly changeDetectorRef: ChangeDetectorRef,
    readonly datePipe: DatePipe,
    dialog: MatDialog,
    breakpointObserver: BreakpointObserver,
    readonly authService: AuthService,
    readonly logger: Logger,
  ) {
    super(store, changeDetectorRef, datePipe, dialog, breakpointObserver, authService, logger);
  }

  getSubmitActionForTask(task: EngagementTask, updatedEngagementLetterData: string): any {
    const isMultiIdentity = this.store.selectSnapshot(EngagementsState.isMultipleIdentitySigningActive);

    if (isMultiIdentity) {
      return new SaveEngagementLetterMultiContact(updatedEngagementLetterData);
    }

    return new SaveEngagementLetter(updatedEngagementLetterData);
  }

  getNecessaryTasks(userTasks: EngagementTask[]): EngagementTask[] {
    // Read is always optional or completed, need to only look at sign
    return userTasks.filter(task => task.type === EngagementTaskType.SIGN_ENGAGEMENT_DOCUMENT);
  }

  /**
   * Annotations are filtered by the current user's document contact id only if the signature requirement type is ALL. Otherwise, only one
   * annotation is generated for the document, and it is not linked with an ID.
   * @param annotations
   */
  filterAdditionalAnnotationsByStep(annotations: Annotation[]): Annotation[] {
    const engagement = this.store.selectSnapshot(EngagementsState.getSelectedEngagement);
    const stepDocumentId = (this.details().tasks?.find(task => task.documentId))?.documentId;
    const fallbackType = STEP_LABEL_DOCUMENT_TYPE_MAPPING[this.details().type];

    const documentInfo = stepDocumentId
      ? engagement.documentsInfoById?.[stepDocumentId] ?? engagement.documentsInfo[fallbackType]
      : engagement.documentsInfo[fallbackType];

    if (documentInfo.signatureRequirementType === SignatureRequirementType.ALL) {
      const isMultiIdentity = this.store.selectSnapshot(EngagementsState.isMultipleIdentitySigningActive);

      // Show annotations for ALL user contacts so each gets a "Sign Here" field
      if (isMultiIdentity) {
        const allUserDocContactIds = this.store.selectSnapshot(EngagementsState.getAllUserDocumentContactIds(documentInfo.documentId));
        
        return annotations.filter(annotation => allUserDocContactIds.includes(getAnnotationDocumentContactId(annotation)));
      }

      return annotations.filter(annotation => getAnnotationDocumentContactId(annotation) === documentInfo.documentContactId);
    }

    return annotations;
  }
}
